import { useMutation, useQueryClient } from '@tanstack/react-query';
import { menuApi, menuKeys, type Menu, type MenuProduct } from '@/entities/menu';

/**
 * Delete a product, optimistically removing it from the catalog.
 *
 * `DELETE /products/{id}` is a SOFT delete of the merchant-owned product — it
 * retires the product everywhere, not just in the branch being viewed. That is
 * deliberately different from unchecking branches in the editor, which only
 * drops the per-branch overlays and leaves the product itself alive; the
 * drawer's destructive action is the one that removes it outright.
 *
 * Optimistic because a removal is unambiguous — there is no server-assigned
 * state to guess — so the patch cannot disagree with the outcome, and it is
 * rolled back if the request fails.
 *
 * NOT `useOptimisticMutation`: that helper reads and writes ONE exact key, and
 * the catalog is cached per scope («Все филиалы» and each branch) and per
 * filter set. A prefix key matches nothing in `getQueryData`/`setQueryData`, so
 * the optimistic removal would silently stop happening while invalidation
 * still worked. Since the delete retires the product in every branch, every
 * cached entry is patched.
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();
  const catalogKey = menuKeys.catalogs();

  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (id: MenuProduct['id']) => menuApi.deleteProduct(id),

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: catalogKey });
      const previous = queryClient.getQueriesData<Menu>({ queryKey: catalogKey });
      for (const [key, menu] of previous) {
        // Same guard as the upsert loops: the prefix also covers the taxonomy
        // list, which is not a `Menu` — patching it threw and killed the
        // mutation before its request fired.
        if (!menu || !Array.isArray(menu.products)) continue;
        queryClient.setQueryData<Menu>(key, {
          ...menu,
          products: menu.products.filter((product) => product.id !== id),
        });
      }
      return { previous };
    },

    onError: (_error, _id, context) => {
      for (const [key, menu] of context?.previous ?? []) {
        queryClient.setQueryData(key, menu);
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKey });
    },
  });
}
